import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { verifyShortCode } from '../../services/shortCodeLogin';
import { useAuthStore } from '../../stores/authStore';
import { AccentButton } from '../components/Button';
import Input from '../components/Input';
import ThemeSwitch from '../components/ThemeSwitch';
import { handleApiErrorWithMessages } from '../../utils/apiErrorHandler';

const ShortCodeVerifyPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [generalError, setGeneralError] = useState(null);
  const [challengeId, setChallengeId] = useState(null);
  const [email, setEmail] = useState('');
  const navigate = useNavigate();
  const { setTokens, refreshUserData } = useAuthStore();

  useEffect(() => {
    // Get challenge_id and email from sessionStorage
    const storedChallengeId = sessionStorage.getItem('shortCodeChallengeId');
    const storedEmail = sessionStorage.getItem('shortCodeEmail');
    
    if (!storedChallengeId) {
      setGeneralError('No active code request. Please request a new code.');
      setTimeout(() => {
        navigate('/short-code-request');
      }, 2000);
      return;
    }
    
    setChallengeId(storedChallengeId);
    setEmail(storedEmail || '');
  }, [navigate]);

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm();

  // Register with custom onChange to auto-uppercase
  const codeRegister = register('code', {
    required: 'Code is required',
    minLength: {
      value: 6,
      message: 'Code must be 6 characters',
    },
    maxLength: {
      value: 6,
      message: 'Code must be 6 characters',
    },
    pattern: {
      value: /^[A-Z0-9]{6}$/,
      message: 'Code must be 6 alphanumeric characters',
    },
  });

  const handleCodeChange = (e) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6);
    setValue('code', value, { shouldValidate: true });
    codeRegister.onChange(e);
  };

  const onSubmit = async (data) => {
    if (!challengeId) {
      setGeneralError('No active code request. Please request a new code.');
      navigate('/short-code-request');
      return;
    }

    setIsLoading(true);
    setGeneralError(null);
    clearErrors();
    
    try {
      const result = await verifyShortCode(challengeId, data.code);
      
      // Set tokens and refresh user data
      setTokens(result.access_token, result.refresh_token);
      await refreshUserData();
      
      // Clear session storage
      sessionStorage.removeItem('shortCodeChallengeId');
      sessionStorage.removeItem('shortCodeEmail');
      
      navigate('/my');
    } catch (error) {
      const { generalError: apiGeneralError, fieldErrors } = handleApiErrorWithMessages(error);
      
      // Set field-specific errors
      Object.keys(fieldErrors).forEach((field) => {
        setError(field, {
          type: 'server',
          message: fieldErrors[field],
        });
      });
      
      // Set general error if present
      if (apiGeneralError) {
        setGeneralError(apiGeneralError);
      } else if (Object.keys(fieldErrors).length === 0) {
        setGeneralError('Invalid code. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = () => {
    // Clear current challenge and redirect to request page
    sessionStorage.removeItem('shortCodeChallengeId');
    navigate('/short-code-request');
  };

  if (!challengeId) {
    return null; // Will redirect in useEffect
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-white dark:bg-[#121212] py-12 px-4 sm:px-6 relative">
      {/* Theme Switch - Top Right */}
      <div className="absolute top-4 right-4 sm:top-6 sm:right-6">
        <ThemeSwitch />
      </div>
      
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-3xl font-bold text-[#121212] dark:text-[#D0D0D0]">
            Enter your code
          </h1>
          <p className="mt-2 text-sm text-[#7B7B7B] dark:text-[#848484]">
            We sent a 6-character code to {email || 'your email'}
          </p>
        </div>

        {/* Form */}
        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          {/* General Error Display */}
          {generalError && (
            <div className="rounded-xl border border-red-500 bg-red-50 dark:bg-red-900/20 p-4">
              <p className="text-sm text-red-600 dark:text-red-400">{generalError}</p>
            </div>
          )}
          
          <div className="space-y-4">
            {/* Code Field */}
            <Input
              type="text"
              label="Code"
              placeholder="Enter 6-character code"
              autoComplete="off"
              maxLength={6}
              error={errors.code?.message}
              className="text-center text-2xl font-mono tracking-widest"
              {...codeRegister}
              onChange={handleCodeChange}
            />
          </div>

          {/* Submit Button */}
          <div>
            <AccentButton
              type="submit"
              variant="primary"
              disabled={isLoading}
              className="w-full"
            >
              {isLoading ? 'Verifying...' : 'Verify code'}
            </AccentButton>
          </div>

          {/* Resend Code */}
          <div className="text-center">
            <button
              type="button"
              onClick={handleResendCode}
              className="text-sm text-[#7B7B7B] dark:text-[#848484] hover:text-[#121212] dark:hover:text-[#D0D0D0] hover:underline"
            >
              Didn't receive code? Request a new one
            </button>
          </div>

          {/* Back to Sign In */}
          <div className="text-center">
            <Link
              to="/sign-in"
              className="text-sm text-[#7B7B7B] dark:text-[#848484] hover:text-[#121212] dark:hover:text-[#D0D0D0] hover:underline"
            >
              Back to sign in
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ShortCodeVerifyPage;
